import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useCart } from '@/contexts/CartContext';
import { usePriceFormat } from '@/hooks/usePriceFormat';
import { useAuth } from '@/contexts/AuthContext';
import { useOrderHistory } from '@/contexts/OrderHistoryContext';
import { apiService } from '@/services/api';
import Card from '@/components/atoms/Card';
import Button from '@/components/atoms/Button';
import Icon from '@/components/atoms/Icon/Icon';
import Spacer from '@/components/atoms/Spacer';
import Loader from '@/components/atoms/Loader';
import RegistrationEncouragement from '@/components/molecules/RegistrationEncouragement';
import { CartItemOut, CartOut, OrderOut } from '@/types/api';
import { calculatePriceWithTax } from '@/utils/taxUtils';
import './Cart.scss';

type PaymentProvider = 'stripe' | 'cash';

// Must match ABANDONED_CARD_ORDER_MAX_AGE_MINUTES in backend/app/order_cleanup.py -
// that's when an unconfirmed card order gets auto-deleted and its stock restored.
const CARD_PAYMENT_TIMEOUT_SECONDS = 5 * 60;

// The backend serializes order.created_at from datetime.utcnow() - a naive
// datetime with no timezone info, e.g. "2026-09-10T14:23:01.123456" (no "Z").
// `new Date(...)` parses a timezone-less ISO string as LOCAL time, not UTC,
// which silently shifts the parsed instant by the viewer's UTC offset. Force
// UTC interpretation unless the string already carries explicit tz info.
const parseAsUtc = (isoString: string): number => {
  const hasTimezone = /Z$|[+-]\d{2}:?\d{2}$/.test(isoString);
  return new Date(hasTimezone ? isoString : `${isoString}Z`).getTime();
};

// Formulario embebido de Stripe (antes vivía en StripePayment.tsx)
const InlineStripeForm: React.FC<{ orderId: number; clientSecret: string }> = ({ orderId, clientSecret }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsSubmitting(true);
    setErrorMessage(null);

    const { error } = await elements.submit();
    if (error) {
      setErrorMessage(error.message ?? t('checkout.paymentError'));
      setIsSubmitting(false);
      return;
    }

    const returnUrl = `${window.location.origin}/order-confirmation/${orderId}?payment=success`;
    const { error: confirmError } = await stripe.confirmPayment({
      elements,
      clientSecret,
      confirmParams: { return_url: returnUrl },
    });

    if (confirmError) {
      setErrorMessage(confirmError.message ?? t('checkout.paymentError'));
      setIsSubmitting(false);
    } else {
      navigate(`/order-confirmation/${orderId}?payment=success`);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="stripe-payment-form">
      <PaymentElement />
      {errorMessage && (
        <p className="stripe-payment-form__error" role="alert">
          {errorMessage}
        </p>
      )}
      <Spacer size="md" />
      <Button
        type="submit"
        variant="primary"
        size="lg"
        fullWidth
        disabled={!stripe || !elements || isSubmitting}
        className="cart-payment__pay"
      >
        {isSubmitting ? t('checkout.processing') : t('checkout.pay')}
      </Button>
    </form>
  );
};

const Cart: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { cart, updateCartItem, removeFromCart, clearCart, checkout, isLoading, currentEventId, currentVenueId } = useCart();
  const { formatPrice, formatWithTax } = usePriceFormat();
  const { isAuthenticated } = useAuth();
  const { addOrder } = useOrderHistory();
  // const [updatingItems, setUpdatingItems] = useState<{ [key: string]: boolean }>({});
  const [removingItems, setRemovingItems] = useState<{ [key: string]: boolean }>({});
  const [showRegistrationEncouragement, setShowRegistrationEncouragement] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<PaymentProvider>('stripe');
  const [localCart, setLocalCart] = useState<CartOut | null>(null);
  const [showCashPayment, setShowCashPayment] = useState(true);

  // Stripe embebido
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [stripeClientSecret, setStripeClientSecret] = useState<string | null>(null);
  const [stripeOrderId, setStripeOrderId] = useState<number | null>(null);
  const [isCreatingIntent, setIsCreatingIntent] = useState(false);
  const [stripeInitError, setStripeInitError] = useState<string | null>(null);
  const [paymentStarted, setPaymentStarted] = useState(false);
  const [stripeOrderCreatedAt, setStripeOrderCreatedAt] = useState<string | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      setShowRegistrationEncouragement(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isProcessing) {
      setLocalCart(cart);
    }
  }, [cart, isProcessing]);

  useEffect(() => {
    const fetchEventConfigs = async () => {
      if (!currentEventId) return;
      try {
        const response = await apiService.getEventConfigs(currentEventId);
        const cashPaymentConfig = response.configs.find(
          config => config.config_key === 'show_cash_payment_option'
        );
        if (cashPaymentConfig) {
          const isCashEnabled = String(cashPaymentConfig.config_value).toLowerCase() === 'true';
          setShowCashPayment(isCashEnabled);
          if (!isCashEnabled) {
            setSelectedProvider((prev) => prev === 'cash' ? 'stripe' : prev);
          }
        }
      } catch (err: any) {
        console.error('Failed to fetch event configs:', err);
        setShowCashPayment(true);
      }
    };
    fetchEventConfigs();
  }, [currentEventId]);

  // Carga la config pública de Stripe una vez
  useEffect(() => {
    const loadStripeConfig = async () => {
      try {
        const config = await apiService.getPaymentsConfig();
        setStripePromise(loadStripe(config.publishable_key));
      } catch (err) {
        console.error('Failed to load Stripe config:', err);
      }
    };
    loadStripeConfig();
  }, []);

  // Countdown until the card order gets auto-deleted if payment isn't confirmed.
  useEffect(() => {
    if (!paymentStarted || !stripeOrderCreatedAt) {
      setRemainingSeconds(null);
      return;
    }

    const computeRemaining = () => {
      const elapsedSeconds = (Date.now() - parseAsUtc(stripeOrderCreatedAt)) / 1000;
      return Math.max(0, Math.round(CARD_PAYMENT_TIMEOUT_SECONDS - elapsedSeconds));
    };

    setRemainingSeconds(computeRemaining());
    const intervalId = setInterval(() => {
      setRemainingSeconds(computeRemaining());
    }, 1000);

    return () => clearInterval(intervalId);
  }, [paymentStarted, stripeOrderCreatedAt]);

  const handleRestartAfterTimeout = () => {
    setPaymentStarted(false);
    setStripeClientSecret(null);
    setStripeOrderId(null);
    setStripeOrderCreatedAt(null);
    setIsProcessing(false);
    if (currentEventId && currentVenueId) {
      navigate(`/events/${currentEventId}/venues/${currentVenueId}`);
    } else {
      navigate('/events');
    }
  };

  const displayCart = cart || localCart;

  // A combo (base drink + mixer) is two cart items sharing combo_group_id.
  // Show it as one line with the combo's total, so the customer sees what
  // they ordered as a single drink (#118).
  const cartRows: { key: string; items: CartItemOut[] }[] = [];
  (displayCart?.items || []).forEach((item) => {
    const existing = item.combo_group_id ? cartRows.find((row) => row.key === `combo-${item.combo_group_id}`) : undefined;
    if (existing) {
      existing.items.push(item);
    } else {
      cartRows.push({ key: item.combo_group_id ? `combo-${item.combo_group_id}` : `${item.item_type}-${item.id}`, items: [item] });
    }
  });

  const itemTaxRate = (item: CartItemOut) =>
    item.tax_breakdown?.formatted_total_tax
      ? item.tax_breakdown.combined_tax_rate
      : item.combined_tax_rate !== null && item.combined_tax_rate !== undefined
        ? item.combined_tax_rate
        : item.tax_rate_at_time;

  const itemTotal = (item: CartItemOut) => calculatePriceWithTax(item.price_at_time * item.quantity, itemTaxRate(item)).total;

  const renderItemCard = (item: CartItemOut) => (
    <Card padding="none" key={`${item.item_type}-${item.id}`} variant="underlined" className="cart-item">
      <div className="cart-item__content">
        <div className="cart-item__info">
          <h3 className="cart-item__name">
            {item.drink_name || item.food_name}
            <span className="cart-item__quantity-number"> x {item.quantity}</span>
          </h3>
          <div className="cart-item__price">
            {item.tax_breakdown?.formatted_total_tax
              ? formatWithTax(item.price_at_time * item.quantity, item.tax_breakdown.combined_tax_rate)
              : formatWithTax(
                  item.price_at_time * item.quantity,
                  item.combined_tax_rate !== null && item.combined_tax_rate !== undefined
                    ? item.combined_tax_rate
                    : item.tax_rate_at_time
                )}
          </div>
          {(item.tax_breakdown || (item.country_tax_rate !== null && item.country_tax_rate !== undefined)) && (
            <div className="cart-item__tax-breakdown">
              {item.tax_breakdown ? (
                <>
                  <span className="cart-item__tax-info">{t('order.itemTaxRate')}: {item.tax_breakdown.item_tax_rate.toFixed(2)}%</span>
                  <span className="cart-item__tax-info">{t('order.countryTaxRate')}: {item.tax_breakdown.country_tax_rate.toFixed(2)}%</span>
                  <span className="cart-item__tax-info cart-item__tax-info--combined">{t('order.combinedTaxRate')}: {item.tax_breakdown.combined_tax_rate.toFixed(2)}%</span>
                </>
              ) : (
                <>
                  <span className="cart-item__tax-info">{t('order.itemTaxRate')}: {item.tax_rate_at_time.toFixed(2)}%</span>
                  <span className="cart-item__tax-info">{t('order.countryTaxRate')}: {item.country_tax_rate!.toFixed(2)}%</span>
                  {item.combined_tax_rate !== null && item.combined_tax_rate !== undefined && (
                    <span className="cart-item__tax-info cart-item__tax-info--combined">{t('order.combinedTaxRate')}: {item.combined_tax_rate.toFixed(2)}%</span>
                  )}
                </>
              )}
            </div>
          )}
          <div className="cart-item__quantity">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleRemoveItem(item.id)}
              disabled={removingItems[item.id] || paymentStarted}
              className="cart-item__remove"
            >
              <Icon name="basurero" size={20} />
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );

  const renderComboCard = (items: CartItemOut[]) => {
    const [base] = items;
    // Removing any part removes the whole combo on the backend
    const isRemoving = items.some((item) => removingItems[item.id]);
    return (
      <Card padding="none" key={`combo-${base.combo_group_id}`} variant="underlined" className="cart-item cart-item--combo">
        <div className="cart-item__content">
          <div className="cart-item__info">
            <h3 className="cart-item__name">
              {items.map((item) => item.drink_name || item.food_name).join(' + ')}
              <span className="cart-item__quantity-number"> x {base.quantity}</span>
            </h3>
            <div className="cart-item__price">
              {formatPrice(items.reduce((sum, item) => sum + itemTotal(item), 0))}
            </div>
            <div className="cart-item__quantity">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleRemoveItem(base.id)}
                disabled={isRemoving || paymentStarted}
                className="cart-item__remove"
                aria-label={t('cart.removeFromCart')}
              >
                <Icon name="basurero" size={20} />
              </Button>
            </div>
          </div>
        </div>
      </Card>
    );
  };

  const handleStartPayment = async () => {
    if (!displayCart || !displayCart.items || displayCart.items.length === 0) return;

    setPaymentStarted(true);
    setIsProcessing(true);
    setIsCreatingIntent(true);
    setStripeInitError(null);
    try {
      const order = await checkout('pending', 'card');
      const { client_secret } = await apiService.createPaymentIntent({
        amount: displayCart.total_amount,
        order_id: order.id,
        description: `Order #${order.id}`,
      });
      setStripeOrderId(order.id);
      setStripeOrderCreatedAt(order.created_at);
      setStripeClientSecret(client_secret);
    } catch (error: any) {
      console.error('Error starting Stripe payment:', error);
      setStripeInitError(error.response?.data?.detail || t('checkout.paymentError'));
      setIsProcessing(false);
      setPaymentStarted(false);
      setStripeOrderCreatedAt(null);
    } finally {
      setIsCreatingIntent(false);
    }
  };

  /* 
  const handleQuantityChange = async (itemId: number, newQuantity: number) => {
    if (newQuantity < 1) return;
    setUpdatingItems(prev => ({ ...prev, [itemId]: true }));
    try {
      await updateCartItem(itemId, newQuantity);
    } catch (error) {
      console.error('Error updating cart item:', error);
    } finally {
      setUpdatingItems(prev => ({ ...prev, [itemId]: false }));
    }
  }; 
  */

  const handleRemoveItem = async (itemId: number) => {
    setRemovingItems(prev => ({ ...prev, [itemId]: true }));
    try {
      await removeFromCart(itemId);
    } catch (error) {
      console.error('Error removing item:', error);
    } finally {
      setRemovingItems(prev => ({ ...prev, [itemId]: false }));
    }
  };

  const handleCashCheckout = async () => {
    try {
      setIsProcessing(true);
      const cartData = cart || localCart;
      if (!cartData) {
        throw new Error('No hay items en el carrito');
      }

      let order: OrderOut;
      if (isAuthenticated) {
        if (!currentEventId) {
          throw new Error('No event ID set for checkout');
        }
        order = await apiService.checkoutCart(currentEventId, 'pending', 'cash');
      } else {
        if (!currentEventId) {
          throw new Error('No event ID set for checkout');
        }
        order = await apiService.checkoutAnonymousCart(currentEventId, cartData, 'pending', 'cash');
      }

      addOrder(order);
      await clearCart();
      navigate(`/order-confirmation/${order.id}?payment=cash`);
    } catch (error: any) {
      console.error('Error en checkout con efectivo:', error);
      alert(error.response?.data?.detail || 'Error al crear el pedido');
      setIsProcessing(false);
    }
  };

  const handleClearCart = async () => {
    if (window.confirm(t('cart.confirmClear'))) {
      try {
        await clearCart();
      } catch (error) {
        console.error('Error clearing cart:', error);
      }
    }
  };

  const handleGoBack = () => {
    if (currentEventId && currentVenueId) {
      navigate(`/events/${currentEventId}/venues/${currentVenueId}`);
    } else if (currentEventId) {
      navigate(`/events/${currentEventId}/venues/0`);
    } else {
      navigate('/events');
    }
  };

  if (isLoading) {
    return <Loader fullScreen size="lg" />;
  }

  if (!displayCart || !displayCart.items || displayCart.items.length === 0) {
    return (
      <div className="container">
        <div className="cart-page">
          <div className="cart-page__header">
            <Button variant="ghost" size="sm" onClick={handleGoBack}>
              <Icon name="atras" size={20} />
            </Button>
            <h1 className="cart-page__title">{t('cart.title')}</h1>
            <Button variant="ghost" size="sm" onClick={() => {}}>
              <Icon name="carrito" size={20} />
            </Button>
          </div>
          <div className="cart-page__empty">
            <div className="cart-page__empty-icon"><Icon name="carrito" size={20} alt={t('cart.title')} /></div>
            <h2 className="cart-page__empty-title">{t('cart.empty')}</h2>
            <p className="cart-page__empty-description">{t('cart.emptyDescription')}</p>
            <Button variant="ghost" size="md" onClick={handleGoBack}>
              <Icon name="atras" size={20} /> {t('common.back')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="cart-page">
        <div className="cart-page__header">
          <Button variant="ghost" size="sm" onClick={handleGoBack}>
            <Icon name="atras" size={20} />
          </Button>
          <h1 className="cart-page__title">{t('cart.title')}</h1>
          <Button variant="ghost" size="sm" onClick={() => {}}>
            <Icon name="carrito" size={20} />
          </Button>
        </div>

        <div className="cart-page__content">
          <div className="cart-page__items">
            <div className="cart-page__items-header">
              <p className="cart-page__items-header__title">{t('cart.itemsTitle')}</p>
            </div>
            {cartRows.map((row) =>
              row.items.length === 1 ? renderItemCard(row.items[0]) : renderComboCard(row.items)
            )}
          </div>

          <div className="cart-page__summary">
            <Card padding="none" variant="underlined" className="cart-summary">
              <div className="cart-summary__details">
                <div className="cart-summary__row">
                  <span>{t('cart.subtotal')}</span>
                  <span>{formatPrice(displayCart.subtotal)}</span>
                </div>
                <div className="cart-summary__row">
                  <span>{t('cart.tax')}</span>
                  <span>{formatPrice(displayCart.total_tax)}</span>
                </div>
                <div className="cart-summary__row">
                  <span>{t('cart.discounts')}</span>
                  <span>{0}</span>
                </div>
                <div className="cart-summary__row cart-summary__total-row">
                  <span>{t('cart.total')}</span>
                  <span className="cart-summary__total">{formatPrice(displayCart.total_amount)}</span>
                </div>
              </div>
            </Card>

           <div className="cart-summary__payment">
              <div className="cart-payment">
                <h3 className="cart-payment__title">{t('checkout.paymentDetails')}</h3>

                {!paymentStarted && (
                  <>
                    {selectedProvider === 'stripe' && (
                      <>
                        <p className="cart-payment__description">{t('checkout.stripeDescription')}</p>
                        <Button
                          variant="primary"
                          size="lg"
                          fullWidth
                          onClick={handleStartPayment}
                          className="cart-payment__pay"
                        >
                          {t('checkout.continueToPayment')} {formatPrice(displayCart.total_amount)}
                        </Button>
                      </>
                    )}
                    {selectedProvider === 'cash' && (
                      <>
                        <p className="cart-payment__description">{t('checkout.cashDescription')}</p>
                        <Button
                          variant="primary"
                          size="lg"
                          fullWidth
                          onClick={handleCashCheckout}
                          disabled={isProcessing}
                          className="cart-payment__pay"
                        >
                          {isProcessing ? t('checkout.processing') : `${t('checkout.createOrder')} ${formatPrice(displayCart.total_amount)}`}
                        </Button>
                      </>
                    )}
                    {showCashPayment && (
                      <button
                        type="button"
                        className="cart-payment__cash-toggle"
                        onClick={() => setSelectedProvider(selectedProvider === 'cash' ? 'stripe' : 'cash')}
                      >
                        <Icon name="signo-dolar" size={16} alt={t('checkout.cash')} />
                        {selectedProvider === 'cash' ? t('checkout.stripe') : t('checkout.cash')}
                      </button>
                    )}
                  </>
                )}

                {paymentStarted && (
                  <>
                    <p className="cart-payment__description">{t('checkout.stripeDescription')}</p>
                    {isCreatingIntent && <Loader size="md" />}
                    {stripeInitError && (
                      <p className="stripe-payment-form__error" role="alert">{stripeInitError}</p>
                    )}
                    {stripeClientSecret && remainingSeconds !== null && (
                      remainingSeconds > 0 ? (
                        <p className="cart-payment__timeout-notice">
                          {t('checkout.paymentTimeoutNotice', {
                            time: `${Math.floor(remainingSeconds / 60)}:${String(remainingSeconds % 60).padStart(2, '0')}`
                          })}
                        </p>
                      ) : (
                        <div className="cart-payment__timeout-expired">
                          <p role="alert">{t('checkout.paymentTimeoutExpired')}</p>
                          <Button variant="primary" size="md" fullWidth onClick={handleRestartAfterTimeout}>
                            {t('checkout.paymentTimeoutRestart')}
                          </Button>
                        </div>
                      )
                    )}
                    {stripePromise && stripeClientSecret && stripeOrderId && (remainingSeconds === null || remainingSeconds > 0) && (
                      <Elements stripe={stripePromise} options={{ clientSecret: stripeClientSecret }}>
                        <InlineStripeForm orderId={stripeOrderId} clientSecret={stripeClientSecret} />
                      </Elements>
                    )}
                    <div className="cart-payment__security">
                      <Icon name="seguro" size={24} alt="Secure" />
                      <span>{t('checkout.securePayment')}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Temporalmente deshabilitado - ver conversación sobre auto-checkout al entrar a /cart
            <div className="cart-summary__actions">
              <Button
                variant="outline"
                size="md"
                onClick={handleClearCart}
                disabled={isLoading || isProcessing}
                className="cart-summary__clear"
              >
                {t('cart.clearCart')}
              </Button>
            </div>
            */}
          </div>
          

          {showRegistrationEncouragement && (
            <RegistrationEncouragement
              variant="compact"
              onSkip={() => setShowRegistrationEncouragement(false)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Cart;